import { useEffect, useState } from 'react'
import { ISelectorOption } from '@trivie/core'
import { UserApi, TopicApi, QuizApi } from '@trivie/core/src/services/api-objects'
import { GroupApi } from '@trivie/core/src/services/api-objects/GroupApi'
import { WorkflowApi } from '@trivie/core/src/services/api-objects/WorkflowApi'
import { AssignmentApi } from '@trivie/core/src/services/api-objects/AssignmentApi'
import { SelectorVariant } from '../autocomplete-selector.types'

const getColumnValue = (columns: any[], field: string) => {
  return columns?.find((c: any) => c.field === field)?.value
}

const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`

export const transformUsers = (data: any): ISelectorOption[] => {
  if (!data?.rows) return []
  return data.rows
    .map((user: any) => {
      const first = getColumnValue(user.columns, 'first_name')
      const last = getColumnValue(user.columns, 'last_name')
      const email = getColumnValue(user.columns, 'email')

      const label = first && last ? `${first} ${last}`.trim() : email
      const subLabel = !first && !last ? '' : email

      return {
        id: user.id,
        type: 'Users',
        label,
        subLabel: subLabel,
        photo_url: getColumnValue(user.columns, 'photo_url') ?? user.photo_url,
      }
    })
    .sort((a: ISelectorOption, b: ISelectorOption) => a.label.localeCompare(b.label))
}

export const transformGroups = (data: any): ISelectorOption[] => {
  if (!data?.rows) return []
  return data.rows
    .map((group: any) => {
      const userCount = getColumnValue(group.columns, 'group_user_count') ?? 0
      return {
        id: group.id,
        type: 'Groups',
        label: getColumnValue(group.columns, 'group_name') ?? '',
        subLabel: pluralize(userCount, 'User', 'Users'),
        childCount: userCount,
      }
    })
    .sort((a: ISelectorOption, b: ISelectorOption) => a.label.localeCompare(b.label))
}

export const transformTopics = (data: any): ISelectorOption[] => {
  if (!data?.rows) return []
  return data.rows.map((topic: any) => {
    const quizCount = getColumnValue(topic.columns, 'quiz_count') ?? 0
    return {
      id: topic.id,
      type: 'topics',
      label: getColumnValue(topic.columns, 'topic_title') ?? '',
      subLabel: pluralize(quizCount, 'Quiz', 'Quizzes'),
      photo_url: getColumnValue(topic.columns, 'photo_url'),
    }
  })
}

export const transformQuizzes = (data: any): ISelectorOption[] => {
  if (!data?.rows) return []
  return data.rows
    .filter((quiz: any) => {
      const count = getColumnValue(quiz.columns, 'quiz_question_count')
      return count > 0
    })
    .map((quiz: any) => {
      const questionCount = getColumnValue(quiz.columns, 'quiz_question_count') ?? 0
      return {
        id: quiz.id,
        type: 'quizzes',
        label: getColumnValue(quiz.columns, 'quiz_title') ?? '',
        subLabel: pluralize(questionCount, 'Question', 'Questions'),
      }
    })
}

export const transformAssignmentContent = (
  topicsResp: any,
  quizzesResp: any,
  pathDetailsResp: any,
): ISelectorOption[] => {
  const topicData: ISelectorOption[] = topicsResp?.data?.topics
    ? topicsResp.data.topics.map((tq: any) => ({
        id: tq.topic_id,
        type: 'topics',
        label: tq.topic_title,
        subLabel: pluralize(tq.quizzes?.length ?? 0, 'Quiz', 'Quizzes'),
        subItems: tq.quizzes?.map((q: any) => ({
          id: q.quiz_id,
          type: 'quizzes',
          label: q.quiz_title,
          parentId: tq.topic_id,
          subLabel: pluralize(q.question_count, 'Question', 'Questions'),
        })),
      }))
    : []

  const quizData = transformQuizzes(quizzesResp?.data)

  const pathData: ISelectorOption[] = pathDetailsResp?.data?.paths
    ? pathDetailsResp.data.paths.map((path: any) => {
        const t = path.topics?.length ?? 0
        const q = path.quizzes?.length ?? 0
        const s = path.sources?.length ?? 0
        const parts: string[] = []
        if (t > 0) parts.push(pluralize(t, 'Topic', 'Topics'))
        if (q > 0) parts.push(pluralize(q, 'Quiz', 'Quizzes'))
        if (s > 0) parts.push(pluralize(s, 'Source', 'Sources'))
        return {
          id: path.path_id,
          type: 'learning paths',
          label: path.title,
          subLabel: parts.join(', '),
          subItems: [...(path.topics ?? []), ...(path.quizzes ?? []), ...(path.sources ?? [])].map(
            (item: any, idx: number) => ({
              parentId: path.path_id,
              type: item?.question_count
                ? 'quizzes'
                : item?.file_name
                  ? 'sources'
                  : item?.quiz_count
                    ? 'topics'
                    : '',
              id: idx,
              label: item?.title,
              subLabel: `${item?.quiz_count || item?.question_count || item?.file_name || ''} ${
                item?.quiz_count ? 'Quizzes' : item?.question_count ? 'Questions' : ''
              }`.trim(),
            }),
          ),
        }
      })
    : []

  return [...topicData, ...quizData, ...pathData]
}

const transformPaths = (pathDetailsResp: any): ISelectorOption[] => {
  const pathData: ISelectorOption[] = pathDetailsResp?.data?.paths
    ? pathDetailsResp.data.paths.map((path: any) => {
        const t = path.topics?.length ?? 0
        const q = path.quizzes?.length ?? 0
        const s = path.sources?.length ?? 0
        const parts: string[] = []
        if (t > 0) parts.push(pluralize(t, 'Topic', 'Topics'))
        if (q > 0) parts.push(pluralize(q, 'Quiz', 'Quizzes'))
        if (s > 0) parts.push(pluralize(s, 'Source', 'Sources'))
        return {
          id: path.path_id,
          type: 'learning paths',
          label: path.title,
          subLabel: parts.join(', '),
          subItems: [...(path.topics ?? []), ...(path.quizzes ?? []), ...(path.sources ?? [])].map(
            (item: any, idx: number) => ({
              parentId: path.path_id,
              type: item?.question_count
                ? 'quizzes'
                : item?.file_name
                  ? 'sources'
                  : item?.quiz_count
                    ? 'topics'
                    : '',
              id: idx,
              label: item?.title,
              subLabel: `${item?.quiz_count || item?.question_count || item?.file_name || ''} ${
                item?.quiz_count ? 'Quizzes' : item?.question_count ? 'Questions' : ''
              }`.trim(),
            }),
          ),
        }
      })
    : []

  return pathData
}

const transformContentFromColumns = (
  topicsResp: any,
  quizzesResp: any,
  pathDetailsResp: any,
): ISelectorOption[] => {
  const topicData = transformTopics(topicsResp?.data)
  const quizData = transformQuizzes(quizzesResp?.data)

  return [...topicData, ...quizData, ...transformPaths(pathDetailsResp)]
}

type SelectorDataResult = {
  options: ISelectorOption[]
  loading: boolean
}

export const useSelectorData = (
  variant: SelectorVariant,
  externalOptions?: ISelectorOption[],
): SelectorDataResult => {
  const [options, setOptions] = useState<ISelectorOption[]>([])
  const [loading, setLoading] = useState(variant !== 'custom')

  useEffect(() => {
    if (variant === 'custom') {
      setOptions(externalOptions ?? [])
      setLoading(false)
      return
    }

    if (externalOptions) {
      setOptions(externalOptions)
      setLoading(false)
      return
    }

    setLoading(true)

    const fetchData = async () => {
      try {
        switch (variant) {
          case 'users': {
            const resp = await UserApi.getUsers(
              'users',
              false,
              [
                { field: 'first_name' },
                { field: 'last_name' },
                { field: 'email' },
                { field: 'photo_url' },
              ],
              'list_users',
            )
            if (resp.ok && resp.data) {
              setOptions(transformUsers(resp.data))
            }
            break
          }

          case 'groups': {
            const resp = await GroupApi.getAllGroups(
              'groups',
              false,
              [{ field: 'group_name' }, { field: 'group_user_count' }],
              'list_groups',
            )
            if (resp.ok && resp.data) {
              setOptions(transformGroups(resp.data))
            }
            break
          }

          case 'users-groups': {
            const [usersResp, groupsResp] = await Promise.all([
              UserApi.getUsers(
                'users',
                false,
                [
                  { field: 'first_name' },
                  { field: 'last_name' },
                  { field: 'email' },
                  { field: 'photo_url' },
                ],
                'list_users',
              ),
              GroupApi.getAllGroups(
                'groups',
                false,
                [{ field: 'group_name' }, { field: 'group_user_count' }],
                'list_groups',
              ),
            ])
            const users = usersResp.ok && usersResp.data ? transformUsers(usersResp.data) : []
            const groups = groupsResp.ok && groupsResp.data ? transformGroups(groupsResp.data) : []
            setOptions([...groups, ...users])
            break
          }

          case 'content': {
            const [topicsResp, quizzesResp, pathDetailsResp] = await Promise.all([
              TopicApi.getAllTopics(
                'topics',
                false,
                [{ field: 'topic_title' }, { field: 'quiz_count' }],
                'list_topics',
              ),
              QuizApi.getAllQuizzes(
                'quizzes',
                false,
                [{ field: 'quiz_title' }, { field: 'quiz_question_count' }],
                'list_quizzes',
              ),
              WorkflowApi.getAssignmentPathDetails(),
            ])
            if (topicsResp.ok && quizzesResp.ok && pathDetailsResp.ok) {
              setOptions(transformContentFromColumns(topicsResp, quizzesResp, pathDetailsResp))
            }
            break
          }

          case 'learning paths': {
            const resp = await WorkflowApi.getAssignmentPathDetails()
            if (resp.ok && resp.data) {
              setOptions(transformPaths(resp))
            }
            break
          }

          case 'topics': {
            const resp = await TopicApi.getAllTopics(
              'topics',
              false,
              [{ field: 'topic_title' }, { field: 'quiz_count' }, { field: 'photo_url' }],
              'list_topics',
            )
            if (resp.ok && resp.data) {
              setOptions(transformTopics(resp.data))
            }
            break
          }

          case 'quizzes': {
            const resp = await QuizApi.getAllQuizzes(
              'quizzes',
              false,
              [
                { field: 'quiz_title' },
                { field: 'quiz_question_count' },
                { field: 'quiz_topic_names' },
              ],
              'list_quizzes',
            )
            if (resp.ok && resp.data) {
              setOptions(transformQuizzes(resp.data))
            }
            break
          }
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [variant, externalOptions])

  return { options, loading }
}
